import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, FileText, Paperclip, Trash2 } from "lucide-react";
import { api, errText } from "@/lib/api";

const isImage = (type) => (type || "").startsWith("image/");

const Thumb = ({ attachment, canWrite, onDelete }) => {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    let objectUrl;
    let cancelled = false;
    api
      .get(`/attachments/${attachment.attachment_id}/download`, { responseType: "blob" })
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setUrl(objectUrl);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment.attachment_id]);

  return (
    <div
      data-testid={`attachment-${attachment.attachment_id}`}
      className="relative border border-border rounded-sm overflow-hidden bg-secondary/40 w-28"
    >
      <a
        href={url || undefined}
        target="_blank"
        rel="noreferrer"
        className="block h-24 grid place-items-center"
        title={attachment.original_filename}
      >
        {isImage(attachment.content_type) && url ? (
          <img
            src={url}
            alt={attachment.original_filename}
            className="h-24 w-28 object-cover"
          />
        ) : (
          <FileText className="h-8 w-8 text-muted-foreground" />
        )}
      </a>
      <p className="px-2 py-1 text-[0.65rem] truncate">{attachment.original_filename}</p>
      {canWrite ? (
        <button
          data-testid={`delete-attachment-${attachment.attachment_id}`}
          onClick={() => onDelete(attachment.attachment_id)}
          className="absolute top-1 right-1 h-7 w-7 grid place-items-center rounded-sm bg-card/90 text-destructive hover:bg-destructive/15 transition-colors duration-200"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  );
};

export const AttachmentPanel = ({ logId, attachments = [], canWrite, onChange, compact = false }) => {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = useCallback(
    async (files) => {
      if (!files?.length) return;
      setBusy(true);
      try {
        for (const file of Array.from(files)) {
          if (file.size > 10 * 1024 * 1024) {
            toast.error(`${file.name} is over 10 MB`);
            continue;
          }
          const body = new FormData();
          body.append("file", file);
          await api.post(`/logs/${logId}/attachments`, body, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }
        toast.success("Attachment saved");
        onChange?.();
      } catch (e) {
        toast.error(errText(e));
      } finally {
        setBusy(false);
        if (fileRef.current) fileRef.current.value = "";
        if (cameraRef.current) cameraRef.current.value = "";
      }
    },
    [logId, onChange],
  );

  const remove = async (id) => {
    try {
      await api.delete(`/attachments/${id}`);
      toast.success("Attachment removed");
      onChange?.();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  return (
    <div className="space-y-3" data-testid={`attachment-panel-${logId}`}>
      {attachments.length ? (
        <div className="flex flex-wrap gap-2">
          {attachments.map((a) => (
            <Thumb key={a.attachment_id} attachment={a} canWrite={canWrite} onDelete={remove} />
          ))}
        </div>
      ) : compact ? null : (
        <p className="text-sm text-muted-foreground">No orders or file photos attached yet.</p>
      )}

      {canWrite ? (
        <div className="flex flex-wrap gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/*,application/pdf"
            multiple
            hidden
            data-testid={`attachment-input-${logId}`}
            onChange={(e) => upload(e.target.files)}
          />
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            data-testid={`attachment-camera-${logId}`}
            onChange={(e) => upload(e.target.files)}
          />
          <button
            type="button"
            data-testid={`attach-file-btn-${logId}`}
            disabled={busy}
            onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-border rounded-sm text-sm font-medium hover:bg-accent transition-colors duration-200"
          >
            <Paperclip className="h-4 w-4" /> {busy ? "Uploading…" : "Attach order / PDF"}
          </button>
          <button
            type="button"
            data-testid={`attach-camera-btn-${logId}`}
            disabled={busy}
            onClick={() => cameraRef.current?.click()}
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-border rounded-sm text-sm font-medium hover:bg-accent transition-colors duration-200"
          >
            <Camera className="h-4 w-4" /> Photograph file
          </button>
        </div>
      ) : null}
    </div>
  );
};
